/**
 * AddressIntel — read-only "what we already know about this address" surface.
 *
 * Implements P2.5 (slim) — two visual treatments rendered together for A/B
 * comparison, then one will be kept and the other dropped.
 *
 * Data: GET /api/access-codes/lookup?order_id={id} (already exists from P2.2).
 * Each returned code carries `provenance: 'address' | 'community'`,
 * `community_name`, `code`, `type`, `instructions`.
 *
 * Treatments:
 *   • variant="pill"  → tiny single-line heads-up. "🔐 7890 (Downtown LV HOA)".
 *   • variant="panel" → fuller read-only block: community badge + each code
 *                       row with provenance, instructions, verified/used time.
 *
 * Both surfaces are non-interactive. Edits live in Settings → Access Codes.
 * The "Manage in Settings ↗" link is the only outbound affordance; it
 * deep-links to the Access Codes panel and selects the community.
 */
import React from 'react';
import { useNavigate } from 'react-router';
import { KeyRound, DoorClosed, Lock, Building2, ExternalLink, UserRound } from 'lucide-react';
import { useAccessCodeLookup } from '@/hooks/useAccessCodeLookup';
import { useAuth } from '@/context/AuthContext';
import { hasSettingsCapability, SETTINGS_ACCESS } from '@/utils/settingsAccess';
import AccessCodeLookupStatus from './AccessCodeLookupStatus';

// ── helpers ─────────────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(mo / 12)}y ago`;
}

function CodeIcon({ type, className = 'h-3.5 w-3.5' }) {
  if (type === 'gate') return <KeyRound className={`${className} text-amber-600`} />;
  if (type === 'door') return <DoorClosed className={`${className} text-blue-600`} />;
  return <Lock className={className} />;
}

// ── shared data hook ────────────────────────────────────────────────────────

function getCommunities(codes) {
  // Derive a single representative community (most lookups return codes
  // from at most one community, but a place_id could theoretically belong
  // to multiple — surface them all uniquely). Tracks both id (for the
  // deep-link) and name (for display).
  const seen = new Map();
  for (const c of codes) {
    if (c.provenance === 'community' && c.community_id && !seen.has(c.community_id)) {
      seen.set(c.community_id, c.community_name || '');
    }
  }
  return Array.from(seen, ([id, name]) => ({ id, name }));
}

// ────────────────────────────────────────────────────────────────────────────
// Variant A — "pill" (my recommendation)
// One-line condensed heads-up. Sits inline near the address.
// ────────────────────────────────────────────────────────────────────────────
function AddressIntelPill({ codes, communities, guardGated, canManage }) {
  const navigate = useNavigate();

  const goToSettings = () => {
    const params = new URLSearchParams();
    if (communities[0]?.id) params.set('community', communities[0].id);
    navigate(`/settings?section=access-codes${params.toString() ? '&' + params.toString() : ''}`);
  };

  const hasAnything = codes.length > 0 || communities.length > 0 || guardGated;

  if (!hasAnything) {
    return (
      <div className="inline-flex items-center gap-1.5 text-[11px] text-stone-400 italic px-2 py-1" data-testid="address-intel-pill-empty">
        No saved access info for this address.
      </div>
    );
  }

  return (
    <div
      className="inline-flex flex-wrap items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-[11px]"
      data-testid="address-intel-pill"
    >
      {guardGated && (
        <span className="inline-flex items-center gap-1 text-amber-700" data-testid="address-intel-pill-guard">
          <UserRound className="h-3 w-3" />
          <span className="font-semibold">Guard</span>
        </span>
      )}
      {codes.length > 0 && (
        <>
          <span className="font-semibold uppercase tracking-wider text-stone-500">
            {guardGated ? '·' : 'Codes:'}
          </span>
          {codes.slice(0, 3).map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1" data-testid={`address-intel-pill-code-${c.id}`}>
              <CodeIcon type={c.type} className="h-3 w-3" />
              <span className="font-mono font-semibold text-stone-800">{c.code}</span>
            </span>
          ))}
          {codes.length > 3 && (
            <span className="text-stone-400">+{codes.length - 3} more</span>
          )}
        </>
      )}
      {communities.length > 0 && (
        <span className="text-stone-500">· {communities.map((c) => c.name).join(', ')}</span>
      )}
      {canManage && <button
        type="button"
        onClick={goToSettings}
        className="ml-1 inline-flex items-center gap-0.5 text-stone-400 hover:text-stone-700 transition-colors"
        title="Manage in Settings"
        data-testid="address-intel-pill-manage"
      >
        <ExternalLink className="h-3 w-3" />
      </button>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Variant B — "panel" (user's idea, fuller read-only treatment)
// Full-width section with community badge + per-code rows.
// ────────────────────────────────────────────────────────────────────────────
function AddressIntelPanel({ codes, communities, guardGated, canManage }) {
  const navigate = useNavigate();

  if (!codes.length && !communities.length && !guardGated) {
    return (
      <div className="rounded-lg border border-dashed border-stone-200 p-3 text-xs text-stone-400 italic flex items-center gap-2" data-testid="address-intel-panel-empty">
        <Lock className="h-3.5 w-3.5" />
        No saved access info for this address.{canManage ? ' Add via Settings → Access Codes.' : ''}
      </div>
    );
  }

  const goToSettings = (communityId) => {
    const params = new URLSearchParams();
    if (communityId) params.set('community', communityId);
    navigate(`/settings?section=access-codes${params.toString() ? '&' + params.toString() : ''}`);
  };

  return (
    <div
      className="rounded-lg border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-3 space-y-2.5"
      data-testid="address-intel-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-stone-400" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
            Address Intel
          </span>
          <span className="text-[10px] text-stone-400">(read-only)</span>
        </div>
        {canManage && <button
          type="button"
          onClick={() => goToSettings(communities[0]?.id)}
          className="inline-flex items-center gap-0.5 text-[11px] text-stone-500 hover:text-primary transition-colors"
          data-testid="address-intel-panel-manage"
        >
          Manage in Settings <ExternalLink className="h-3 w-3" />
        </button>}
      </div>

      {guardGated && <div className="flex items-center gap-1 text-xs text-amber-700" data-testid="address-intel-panel-guard"><UserRound className="h-3 w-3" /> Guard</div>}

      {/* Community membership */}
      {communities.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="address-intel-panel-communities">
          <Building2 className="h-3.5 w-3.5 text-stone-400" />
          <span className="text-[11px] text-stone-500">Part of:</span>
          {communities.map((c) => canManage ? (
            <button
              key={c.id}
              type="button"
              onClick={() => goToSettings(c.id)}
              className="text-xs font-medium text-stone-700 hover:text-primary hover:underline transition-colors"
              data-testid={`address-intel-panel-community-${c.id}`}
            >
              {c.name}
            </button>
          ) : <span key={c.id} className="text-xs font-medium text-stone-700">{c.name}</span>)}
        </div>
      )}

      {/* Codes */}
      {codes.length === 0 ? (
        <p className="text-[11px] text-stone-400 italic">No active codes for this address.</p>
      ) : (
        <ul className="space-y-1.5">
          {codes.map((c) => (
            <li
              key={c.id}
              className="rounded-md border border-stone-200 bg-white px-2.5 py-2 space-y-1"
              data-testid={`address-intel-panel-code-${c.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <CodeIcon type={c.type} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                  {c.type}
                </span>
                <span className="font-mono text-sm font-semibold text-stone-900">{c.code}</span>
                <span className={`ml-auto inline-block text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  c.provenance === 'address'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                }`}>
                  {c.provenance === 'address' ? 'this address' : 'community'}
                </span>
              </div>
              {c.instructions && (
                <p className="text-xs text-stone-600 leading-relaxed">{c.instructions}</p>
              )}
              <div className="flex items-center gap-3 text-[10px] text-stone-400">
                {c.verified_at && (
                  <span>Verified {relativeTime(c.verified_at)}{c.verified_by ? ` by ${c.verified_by}` : ''}</span>
                )}
                {c.last_used_at && <span>Used {relativeTime(c.last_used_at)}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Public entry — renders both variants stacked for A/B comparison.
// Pass variant="pill" or "panel" to render only one.
// ────────────────────────────────────────────────────────────────────────────
export default function AddressIntel({ orderId, openingId, addressKey, variant = 'both' }) {
  const lookup = useAccessCodeLookup({ orderId, openingId, addressKey });
  const { user } = useAuth() || {};
  const intel = { ...lookup, communities: getCommunities(lookup.codes), canManage: hasSettingsCapability(user, SETTINGS_ACCESS.MANAGE) };
  if (!orderId) return null;
  const status = <AccessCodeLookupStatus lookup={lookup} testId="address-intel-status" />;
  if (!lookup.hasData) return status;

  if (variant === 'pill') return <>{status}<AddressIntelPill {...intel} /></>;
  if (variant === 'panel') return <>{status}<AddressIntelPanel {...intel} /></>;

  // A/B comparison mode: stack pill on top, panel below, with a labeled
  // separator so the user can tell which is which while deciding.
  return (
    <div className="space-y-3" data-testid="address-intel-ab">
      {status}
      <div className="space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
          Variant A · Pill (compact heads-up)
        </p>
        <AddressIntelPill {...intel} />
      </div>
      <div className="space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
          Variant B · Panel (full read-only block)
        </p>
        <AddressIntelPanel {...intel} />
      </div>
    </div>
  );
}
